import {
  createContext,
  useContext,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cx } from '@parrot/utils';
import { ScrollShadow } from '../ScrollShadow';
import { Skeleton } from '../Skeleton';
import styles from './Table.module.css';

type Align = 'left' | 'center' | 'right';

/* `align` is a legacy HTML attribute typed as string, so it has to be replaced rather
   than extended — the same trap as `title` on Notice and `value` on Tab. */
type AlignProp = { align?: Align };

type Direction = 'asc' | 'desc';

type Sort = { key: string; direction: Direction };

const TableContext = createContext<{
  loading: boolean;
  loadingRows: number;
  sort?: Sort;
  onSortChange?: (sort: Sort) => void;
}>({
  loading: false,
  loadingRows: 5,
});

type TableProps = HTMLAttributes<HTMLTableElement> & {
  /** Swaps the body for shimmer rows. The header keeps rendering, so the shape holds. */
  loading?: boolean;
  loadingRows?: number;
  /** The sorted column, matched against each `Th`'s `sortKey`. Omit for none. */
  sort?: Sort;
  onSortChange?: (sort: Sort) => void;
};

/**
 * A data table. Six flat parts mirroring the HTML, so the structure stays yours.
 *
 * Sorting is strictly controlled: a header click asks for a new `sort`, and nothing
 * moves until it comes back down. The table never reorders rows — that's the caller's,
 * whether in memory or on the server.
 */
export const Table = ({
  loading = false,
  loadingRows = 5,
  sort,
  onSortChange,
  className,
  children,
  ...tableProps
}: TableProps) => (
  <TableContext.Provider value={{ loading, loadingRows, sort, onSortChange }}>
    {/* Wide tables scroll rather than squash, with an edge shadow showing there's more.
        The reference drove that from a resize listener that never re-fired when data
        loaded and the table got wider; ScrollShadow has nothing to fall out of date. */}
    <ScrollShadow axis={'x'} className={styles.scroller}>
      <table className={cx(styles.table, className)} {...tableProps}>
        {children}
      </table>
    </ScrollShadow>
  </TableContext.Provider>
);

export const Thead = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
  <thead className={cx(styles.thead, className)} {...props} />
);

/* colSpan is clamped to the real column count by the browser (the HTML max is 1000), so
   the skeleton never has to know how many columns there are — which is the whole point.
   A separate TableSkeleton made the caller describe their columns twice, and the two
   descriptions drifted. */
const SPAN_ALL = 999;

export const Tbody = ({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) => {
  const { loading, loadingRows } = useContext(TableContext);

  return (
    <tbody className={cx(styles.tbody, className)} {...props}>
      {loading
        ? Array.from({ length: loadingRows }, (_, row) => (
            // Not styles.tr: a shimmer placeholder shouldn't light up on hover.
            <tr key={row}>
              <td className={styles.td} colSpan={SPAN_ALL}>
                <Skeleton height={'0.9em'} />
              </td>
            </tr>
          ))
        : children}
    </tbody>
  );
};

type TrProps = HTMLAttributes<HTMLTableRowElement> & {
  selected?: boolean;
};

/**
 * An `onClick` gives the row a pointer cursor — there's no separate flag to forget.
 * Keyboard users still need a real link or button inside it: a `role="button"` on a
 * `<tr>` costs you the row semantics and still needs a key handler.
 */
export const Tr = ({ selected = false, className, onClick, ...props }: TrProps) => (
  <tr
    className={cx(
      styles.tr,
      onClick !== undefined && styles.clickable,
      selected && styles.selected,
      className,
    )}
    onClick={onClick}
    {...props}
  />
);

const SortIcon = ({ sort }: { sort?: Direction }) => (
  <svg
    className={styles.sortIcon}
    viewBox={'0 0 24 24'}
    fill={'none'}
    stroke={'currentColor'}
    strokeWidth={2.5}
    strokeLinecap={'round'}
    aria-hidden={'true'}
  >
    {sort !== 'desc' && <path d={'m7 11 5-5 5 5'} />}
    {sort !== 'asc' && <path d={'m7 15 5 5 5-5'} />}
  </svg>
);

type ThProps = Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> &
  AlignProp & {
    /** Makes the column sortable. Compared against `Table`'s `sort.key`. */
    sortKey?: string;
    /** The direction a first click asks for. `desc` suits figures, where biggest-first is the question. */
    sortFirst?: Direction;
  };

export const Th = ({
  align = 'left',
  sortKey,
  sortFirst = 'asc',
  scope = 'col',
  className,
  children,
  ...props
}: ThProps) => {
  const { sort, onSortChange } = useContext(TableContext);
  const direction = sortKey !== undefined && sort?.key === sortKey ? sort.direction : undefined;

  return (
    <th
      scope={scope}
      aria-sort={
        direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : undefined
      }
      className={cx(styles.th, styles[align], className)}
      {...props}
    >
      {sortKey !== undefined ? (
        // A real button, so keyboard, focus and activation come from the platform. The
        // reference put onClick on the <th>, which no keyboard can reach.
        <button
          type={'button'}
          className={styles.sortButton}
          onClick={() =>
            onSortChange?.({
              key: sortKey,
              direction: direction === undefined ? sortFirst : direction === 'asc' ? 'desc' : 'asc',
            })
          }
        >
          {children}
          <SortIcon sort={direction} />
        </button>
      ) : (
        children
      )}
    </th>
  );
};

type TdProps = Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> & AlignProp;

export const Td = ({ align = 'left', className, ...props }: TdProps) => (
  <td className={cx(styles.td, styles[align], className)} {...props} />
);
